<script setup lang="ts">
import type { PaginationDto } from "core/dto";

import { ITEMS_PER_PAGE } from "~/common/constants/items-per-page";
import { getKindId } from "~/common/helpers/get-kind-id";
import { queryValueToArrayOfIds } from "~/common/helpers/query-value-to-array-of-ids";
import type { GetAllShopProductsDto } from "~/common/interfaces/dto/shop/get-all-shop-products.dto";
import { useShopStore } from "~/stores/shop";

const { t } = useI18n();
const route = useRoute();
const shopStore = useShopStore();
const isProductsLoading = ref(false);

useSeoMeta({
	title: () => t("meta.shop.title"),
	ogTitle: () => t("meta.shop.ogTitle"),
	description: () => t("meta.shop.description"),
	ogDescription: () => t("meta.shop.ogDescription")
});

const updateProducts = async (dto: GetAllShopProductsDto = {}, paginationDto: Partial<PaginationDto> = {}, mergeProducts = false) => {
	isProductsLoading.value = true;

	if (route.params.kind) {
		const kindName = route.params.kind.toString();
		dto.kind = getKindId(kindName, shopStore.components?.kinds);
	} else {
		dto.kind = undefined;
	}
	dto.colors = queryValueToArrayOfIds(route.query.colors);
	dto.sizes = queryValueToArrayOfIds(route.query.sizes);
	dto.collections = queryValueToArrayOfIds(route.query.collections);
	dto.tags = queryValueToArrayOfIds(route.query.tags);
	paginationDto.search = route.query.search?.toString();

	const res = await shopStore.getAllProducts(dto, { ...paginationDto, limit: ITEMS_PER_PAGE }, mergeProducts);

	isProductsLoading.value = false;

	return res;
};

await useAsyncData("/shop/products", () => updateProducts({}, { page: parseInt(route.query.page?.toString() || "1") }));

watch(() => [route.params, route.query, route.hash], (to, from) => {
	const [toParams, toQuery, toHash] = to;
	const [fromParams, fromQuery, fromHash] = from;

	const fromPage = fromQuery?.page;
	const toPage = toQuery?.page;

	if (toPage && (fromPage !== toPage)) {
		return;
	} else if (
		JSON.stringify(fromParams) === JSON.stringify(toParams) &&
		JSON.stringify(fromQuery) === JSON.stringify(toQuery) &&
		toHash !== fromHash
	) {
		return;
	}

	updateProducts({}, shopStore.recentProductsQuery);
});
</script>

<template>
	<div id="gallery" class="box-content px-6 pt-10 pb-[144px] sm:pt-6 sm:pb-11 xs:pt-6 xs:pb-11 xs:px-4">
		<div class="flex max-w-[1400px] m-auto gap-x-10 md:gap-x-6 sm:flex-col xs:flex-col">
			<ShopFilter class="min-w-[288px]" :components="shopStore.components" />
			<ShopGallery
				class="grow"
				:class="isProductsLoading && 'pointer-events-none'"
				:components="shopStore.components"
				:products="shopStore.products"
				:products-total="shopStore.productsMeta.total"
				@update:page="updateProducts"
			/>
		</div>
		<NuxtPage />
	</div>
</template>
